/**
 * 管理员用户管理相关 Store
 * 负责管理员端用户数据的加载、管理和操作
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminUsersApi } from '@/https'
import type { UserDetail } from '@/https'
import type {
    AdminUserListRequest,
    UserManagement
} from '@/https/types_admin/users'

export const useAdminUsersStore = defineStore('admin-users', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const userList = ref<UserDetail[]>([])
    const currentUserDetail = ref<UserDetail | null>(null)

    // 分页信息
    const userPagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastUserQuery = ref<AdminUserListRequest>({})

    // 加载状态
    const userListLoading = ref(false)
    const userDetailLoading = ref(false)
    const userOperationLoading = ref(false)

    // 错误状态
    const userListError = ref<string | null>(null)
    const userDetailError = ref<string | null>(null)
    const userOperationError = ref<string | null>(null)

    // ===== 计算属性 =====
    const userListByRole = computed(() => {
        const groups: Record<string, UserDetail[]> = {}
        userList.value.forEach(user => {
            const role = user.roles?.name || 'unknown'
            if (!groups[role]) {
                groups[role] = []
            }
            groups[role].push(user)
        })
        return groups
    })

    const userListByStatus = computed(() => {
        const groups: Record<string, UserDetail[]> = {}
        userList.value.forEach(user => {
            const status = user.is_active ? 'active' : 'inactive'
            if (!groups[status]) {
                groups[status] = []
            }
            groups[status].push(user)
        })
        return groups
    })

    const activeUsers = computed(() => userList.value.filter(user => user.is_active))
    const inactiveUsers = computed(() => userList.value.filter(user => !user.is_active))
    const hasMoreUsers = computed(() => userPagination.value.has_more)

    // ===== 数据加载方法 =====

    /**
     * 加载用户列表
     */
    const loadUserList = async (params?: AdminUserListRequest, forceRefresh = false) => {
        const query = { ...lastUserQuery.value, ...params }
        lastUserQuery.value = query

        try {
            userListLoading.value = true
            userListError.value = null

            const response = await cacheStore.smartCache(
                `admin-users:list:${JSON.stringify(query)}`,
                () => adminUsersApi.getUserList(query),
                forceRefresh
            )
            userList.value = response.items
            userPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载用户列表失败'
            userListError.value = errorMessage
            console.error('加载用户列表失败:', error)
        } finally {
            userListLoading.value = false
        }
    }

    /**
     * 加载用户详情
     */
    const loadUserDetail = async (id: number, forceRefresh = false) => {
        try {
            userDetailLoading.value = true
            userDetailError.value = null

            const response = await cacheStore.smartCache(
                `admin-users:detail:${id}`,
                () => adminUsersApi.getUserDetail(id),
                forceRefresh
            )
            currentUserDetail.value = response
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载用户详情失败'
            userDetailError.value = errorMessage
            console.error(`加载用户详情失败 (ID: ${id}):`, error)
        } finally {
            userDetailLoading.value = false
        }
    }

    // ===== CRUD 操作方法 =====

    /**
     * 创建用户
     */
    const createUser = async (data: UserDetail) => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null
            const response = await adminUsersApi.createUser(data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-users:list')
            await loadUserList({}, true) // 重新加载列表
            return response
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '创建用户失败'
            userOperationError.value = errorMessage
            console.error('创建用户失败:', error)
            throw error
        } finally {
            userOperationLoading.value = false
        }
    }

    /**
     * 更新用户
     */
    const updateUser = async (id: number, data: UserManagement) => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null
            await adminUsersApi.updateUser(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-users:list')
            cacheStore.removeCacheByPrefix(`admin-users:detail:${id}`)
            // 重新加载详情
            await loadUserDetail(id, true)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '更新用户失败'
            userOperationError.value = errorMessage
            console.error(`更新用户失败 (ID: ${id}):`, error)
            throw error
        } finally {
            userOperationLoading.value = false
        }
    }

    /**
     * 删除用户（禁用）
     */
    const deleteUser = async (id: number) => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null
            await adminUsersApi.deleteUser(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-users:list')
            cacheStore.removeCacheByPrefix(`admin-users:detail:${id}`)
            await loadUserList({}, true) // 重新加载列表
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '禁用用户失败'
            userOperationError.value = errorMessage
            console.error(`禁用用户失败 (ID: ${id}):`, error)
            throw error
        } finally {
            userOperationLoading.value = false
        }
    }

    // ===== 业务操作方法 =====

    /**
     * 启用用户
     */
    const enableUser = async (id: number, data: UserDetail) => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null
            await adminUsersApi.enableUser(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-users:list')
            cacheStore.removeCacheByPrefix(`admin-users:detail:${id}`)
            await loadUserDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '启用用户失败'
            userOperationError.value = errorMessage
            console.error(`启用用户失败 (ID: ${id}):`, error)
            throw error
        } finally {
            userOperationLoading.value = false
        }
    }

    /**
     * 设置用户角色
     */
    const setUserRole = async (id: number, data: UserDetail) => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null
            await adminUsersApi.setUserRole(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-users:list')
            cacheStore.removeCacheByPrefix(`admin-users:detail:${id}`)
            await loadUserDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '设置用户角色失败'
            userOperationError.value = errorMessage
            console.error(`设置用户角色失败 (ID: ${id}):`, error)
            throw error
        } finally {
            userOperationLoading.value = false
        }
    }

    // ===== 工具方法 =====

    /**
     * 根据ID查找用户
     */
    const findUserById = (id: number) => {
        return userList.value.find(user => user.id === id)
    }

    /**
     * 清除所有状态
     */
    const clearAllStates = () => {
        userList.value = []
        currentUserDetail.value = null
        userPagination.value = { total: 0, page: 1, size: 20, has_more: false }
        lastUserQuery.value = {}
        // 清除错误状态
        userListError.value = null
        userDetailError.value = null
        userOperationError.value = null
    }

    return {
        // 状态
        userList,
        currentUserDetail,
        userPagination,
        lastUserQuery,

        // 加载状态
        userListLoading,
        userDetailLoading,
        userOperationLoading,

        // 错误状态
        userListError,
        userDetailError,
        userOperationError,

        // 计算属性
        userListByRole,
        userListByStatus,
        activeUsers,
        inactiveUsers,
        hasMoreUsers,

        // 数据加载方法
        loadUserList,
        loadUserDetail,

        // CRUD 操作方法
        createUser,
        updateUser,
        deleteUser,

        // 业务操作方法
        enableUser,
        setUserRole,

        // 工具方法
        findUserById,
        clearAllStates
    }
})